import {
  Autocomplete,
  Chip,
  Icon,
  IconButton,
  InputAdornment,
  styled,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  useAddQueryParams,
  useQueryParams,
} from "../../../dataAccess/QueryParams";
import TextField from "../TextField";
import type { Settings as SearchSettings } from "../../public/Search";
import { useTranslation } from "../../../helpers/i18n";

interface Props
  extends Pick<SearchSettings, "alignment" | "multipleSearch" | "input"> {
  autoFocus?: boolean;
  className?: string;
  placeholderText: string;
}

const SEARCH_QUERY_PARAM_KEY = "q";

const StyledTextField = styled(TextField)(({ theme }) => ({
  paddingLeft: 0,
  paddingRight: 0,
  "& .MuiInputBase-input": {
    [theme.breakpoints.up("lg")]: {
      minWidth: "6rem !important",
    },
    flex: 1,
  },
  "& .MuiFilledInput-root.MuiInputBase-root": {
    minHeight: "2.5rem",
    paddingLeft: "0.6rem !important",
    paddingRight: "0.6rem !important",
    marginBottom: "0rem",
  },
}));

const StyledChip = styled(Chip)(() => ({
  maxWidth: "10rem !important",
}));

const StyledIconButton = styled(IconButton)(() => ({
  width: 24,
  height: 24,
}));

const StyledAdornment = styled(InputAdornment)(() => ({
  marginRight: 0,
  marginLeft: 0,
  "&:hover": {
    cursor: "pointer",
  },
  minHeight: 32,
}));

const StyledSearchIcon = styled(Icon)<{ textColor?: string }>(
  ({ theme, textColor }) => ({
    color: textColor || theme.palette.text.inputDefault,
  }),
);

type Value = string | string[];

const SearchBar = ({
  multipleSearch = false,
  alignment,
  input,
  autoFocus,
  className,
  placeholderText = "Search",
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const setQueryParams = useAddQueryParams();
  const { q } = useQueryParams();

  const searchValueFromQueryParam = useMemo(() => {
    if (q) {
      return multipleSearch ? q.split(",") : q;
    }
    return multipleSearch ? [] : "";
  }, [router]);

  const [searchValue, setSearchValue] = useState<Value>(
    searchValueFromQueryParam,
  );

  useEffect(() => {
    setSearchValue(searchValueFromQueryParam);
  }, [searchValueFromQueryParam]);

  const [inputValue, setInputValue] = useState(!multipleSearch && q ? q : "");

  const setSearchQuery = (value: string) => {
    setQueryParams([
      {
        paramKey: "page_number",
        paramValue: null,
      },
      {
        paramKey: SEARCH_QUERY_PARAM_KEY,
        paramValue: value.length ? value : null,
      },
    ]);
  };

  const onSearchClick = (currentValue?: Value) => {
    if (!multipleSearch) {
      setSearchQuery(inputValue);
    } else {
      setSearchValue((prevValue) => {
        const newValue = [...(prevValue as string[]), inputValue];
        if (!currentValue && !inputValue) {
          return prevValue;
        }
        const value = currentValue || newValue;
        setSearchQuery(value.toString());
        return value;
      });
    }
  };
  const autocompleteRef = useRef<HTMLElement>();
  useEffect(() => {
    if (autoFocus) {
      autocompleteRef.current?.querySelector("input")?.focus();
    }
  }, [autoFocus]);

  const { settingsOverride, settingsOverrideColors } = useMemo(() => {
    if (!input) {
      return { settingsOverride: undefined, settingsOverrideColors: undefined };
    }

    return {
      settingsOverride: {
        border: input.border,
        capitalization: input.capitalization,
        showUnderline: input.showUnderline,
        size: input.size,
      },
      settingsOverrideColors: {
        backgroundColor: input.backgroundColor,
        borderColor: input.borderColor,
        textColor: input.textColor,
      },
    };
  }, [input]);
  return (
    <Autocomplete
      className={className}
      multiple={multipleSearch}
      ref={autocompleteRef}
      value={searchValue}
      onChange={(e, newValue) => {
        if (!multipleSearch && newValue === null) {
          setInputValue("");
          return;
        }
        onSearchClick(newValue || "");
      }}
      inputValue={inputValue}
      onInputChange={(e, newInputValue) => {
        setInputValue(newInputValue);
      }}
      limitTags={2}
      options={[]}
      freeSolo
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <StyledChip
            {...getTagProps({ index })}
            key={index}
            label={option}
            title={option}
          />
        ))
      }
      renderInput={(params) => (
        <StyledTextField
          {...params}
          settingsOverride={settingsOverride}
          settingsOverrideColors={settingsOverrideColors}
          placeholder={placeholderText}
          onKeyDown={(e) => {
            if (e.key === "Enter" && inputValue === "" && q) {
              setSearchQuery("");
            }
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              textAlign: alignment === "left" ? "left" : "right",
            },
            role: "searchbox",
            "aria-expanded": undefined,
            "aria-autocomplete": undefined,
          }}
          InputProps={{
            ...params.InputProps,
            ...(alignment === "left"
              ? {
                  startAdornment: (
                    <>
                      <StyledAdornment
                        onClick={() => onSearchClick()}
                        position="end"
                      >
                        <StyledSearchIcon
                          textColor={settingsOverrideColors?.textColor}
                        >
                          search
                        </StyledSearchIcon>
                      </StyledAdornment>
                    </>
                  ),
                }
              : {}),
            endAdornment: (
              <>
                {inputValue && !multipleSearch && (
                  <StyledAdornment position="end">
                    <StyledIconButton
                      size="small"
                      aria-label={t("accessibility_search_clear")}
                      onClick={() => {
                        setInputValue("");
                        setQueryParams([
                          {
                            paramKey: SEARCH_QUERY_PARAM_KEY,
                            paramValue: null,
                          },
                        ]);
                      }}
                    >
                      <Icon>close</Icon>
                    </StyledIconButton>
                  </StyledAdornment>
                )}
                {alignment !== "left" && (
                  <StyledAdornment
                    onClick={() => onSearchClick()}
                    position="end"
                  >
                    <StyledSearchIcon
                      textColor={settingsOverrideColors?.textColor}
                    >
                      search
                    </StyledSearchIcon>
                  </StyledAdornment>
                )}
                {alignment === "left" && params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchBar;
